import React, { useEffect, useMemo, useState } from 'react';
import Form, { InputField } from '../../../../components/form';
import * as yup from 'yup';
import { SelectField, _selectoptionType } from '../../../../components/form/select-field';
import styles from '../../styles.module.scss';
import { DataSourceHandler } from '@api/data-source-handler';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { Flex } from '@components/ui/Flex';
import { Text } from '@components/ui/Text';
import { ToggleField } from '@components/form/toggle-field';
import { ShowWhenTrue } from '@helpers/showwhentrue';
import { TooltipTop } from '@components/tooltips';
import { isEmpty } from 'lodash';
import { infoAlert } from '@components/toastify/notify-toast';
import { InformativeIcon } from '@pages/workflow-page/assets/icons';

const nifiFormSchema = yup.object({
    edgeName: yup.string()
        .test("no-only-spaces", "This field cannot contain only spaces.", (value) => value && value.trim().length > 0)
        .test("no-leading-spaces", "This field cannot start with a space.", (value) => value && value[0] !== " ")
        .required('This is a required field'),
    nodeKind: yup.string().required('This is a required field'),
    storeForwardToggle: yup.boolean(),
    enableScaling: yup.boolean(),
    scaleNodes: yup.number().nullable(),
    notificationEnabled: yup.boolean(),
    notificationEmail: yup.string(),
    autoResumeRequests: yup.boolean(),
});

export type NifiFormValues = yup.InferType<typeof nifiFormSchema>

/*
 * Node kinds that keep requests RUNNING while offline and auto-resume on reconnect by default
 */
const AUTO_RESUME_NODE_KINDS = ['OSIPI', 'OPCHDA', 'AspenIP21'];

/*
 * Resolves the default auto-resume behavior for a node kind, preserving today's hardcoded behavior
 */
function getAutoResumeDefault(nodeKind: string): boolean {
    return AUTO_RESUME_NODE_KINDS.includes(nodeKind);
}

export type SecretFieldConfig = {
    useSecret: boolean;
    data: string;
    secretManagerId: number | null;
};

export type UserFormProps = {
    type: 'UPDATE' | 'ADD';
    initialValues: any;
    onSubmit?: (arg0: NifiFormValues & { secretFields?: Record<string, SecretFieldConfig> }) => any;
    submitButtons?: React.ReactNode;
    secretsList?: string[];
    secretsLoading?: boolean;
    secretManagerId?: number | null;
}

type FieldConfig = {
    key: string;
    label: string;
    infoText?: string;
    type: 'text' | 'password' | 'select' | 'number' | 'file';
    options?: { label: string; value: string }[];
    required?: boolean;
    defaultValue?: string;
    accept?: string;
    readOnly?: boolean;
};

type TableFieldItem = {
    key: string;
    label: string;
    infoText?: string;
    useSecret: boolean;
    value: string;
    required: boolean;
    type: 'text' | 'password' | 'select' | 'number' | 'file';
    options?: { label: string; value: string }[];
    accept?: string;
    file?: File | null;
    fileName?: string;
    readOnly?: boolean;
};

export const NifiForm: React.FC<UserFormProps> = ({
    onSubmit,
    type,
    initialValues,
    submitButtons,
    secretsList = [],
    secretsLoading = false,
    secretManagerId = null,
}) => {
    const [nodeKindOptions, setNodeKindOptions] = useState<_selectoptionType[]>([]);
    const [selectedNodeKind, setSelectedNodeKind] = useState<string>('');
    const [tableFields, setTableFields] = useState<TableFieldItem[]>([]);
    const [storeForwardToggle, setStoreForwardToggle] = useState(true);
    const [enableScaling, setEnableScaling] = useState(false);
    const [scaledNodesCount, setScaledNodesCount] = useState<number | null>(null);
    const [notificationEnabled, setNotificationEnabled] = useState(false);
    const [autoResumeRequests, setAutoResumeRequests] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const { envVariables: Env, allUsersList, activeUserInfo } = useSelector((store: RootState) => store.AccountReducer);

    /*
     * Maps the platform's user list into select options for the email alert recipient picker
     */
    const notificationUserOptions = useMemo(
        () => allUsersList.map((user) => ({
            label: `${user.firstName} ${user.lastName ? user.lastName.charAt(0) : ''} (${user.email})`,
            value: user.email
        })),
        [allUsersList]
    );

    const isSecretField = (field: any) => field && typeof field === 'object' && 'useSecret' in field && field.useSecret === true;
    const getSecretFieldData = (field: any) => {
        if (field && typeof field === 'object' && 'data' in field) {
            return field.data || '';
        }
        return field || '';
    };

    /*
     * Unwraps the secret-field envelope if present, falling back to the node kind's default only when unset
     */
    const resolveAutoResumeRequests = (field: any, nodeKind: string): boolean => {
        const unwrapped = field && typeof field === 'object' && 'data' in field ? field.data : field;
        if (unwrapped === null || unwrapped === undefined || unwrapped === '') {
            return getAutoResumeDefault(nodeKind);
        }
        return unwrapped === true || unwrapped === 'true';
    };

    const getConfigurableFields = (nodeKind: string): FieldConfig[] => {
        const commonFields: FieldConfig[] = [
            { key: 'accessKey', label: 'Access Key', type: 'password', required: true, infoText: "Authentication key for Data Browser connection" },
            { key: 'accessKeyConfirm', label: 'Confirm Access Key', type: 'password', required: true, infoText: 'Confirm your access key' },
        ];

        switch (nodeKind) {
            case 'Relational':
                return [
                    { key: 'rdbmsType', label: 'RDBMS Type', type: 'select', options: [{ label: 'SQL Server', value: 'SQL Server' }, { label: 'MySQL', value: 'MySQL' }], required: true, defaultValue: 'SQL Server', infoText:'Select the relational server type for this connection.' },
                    { key: 'rdbmsHost', label: 'RDBMS Host', type: 'text', required: true, infoText: 'Server hostname or IP.' },
                    { key: 'rdbmsPort', label: 'RDBMS Port', type: 'number', required: true, defaultValue: '', infoText: 'Port number of the server.' },
                    { key: 'rdbmsUser', label: 'RDBMS User', type: 'text', required: true, infoText: 'Username for server access.' },
                    { key: 'rdbmsPassword', label: 'RDBMS Password', type: 'password', required: true, infoText: 'Password for edge access.' },
                    { key: 'rdbmsDatabase', label: 'RDBMS Database', type: 'text', required: true, infoText: 'Enter the name of the target server.' },
                    { key: 'rdbmsConnectionUrl', label: 'Connection URL', type: 'text', required: false, infoText: 'Ensure the formed URL matches your server configuration.' },
                    ...commonFields,
                ];
            case 'AspenIP21':
                return [
                    { key: 'ip21Host', label: 'IP21 Host', type: 'text', required: true, infoText: 'Server hostname or IP.' },
                    { key: 'ip21Port', label: 'IP21 Port', type: 'text', required: true, defaultValue: '', infoText: 'Port number of the server.' },
                    { key: 'ip21User', label: 'IP21 User', type: 'text', required: true, infoText: 'Username for server access.' },
                    { key: 'ip21Password', label: 'IP21 Password', type: 'password', required: true, infoText: 'Password for edge access.' },
                    ...commonFields,
                ];
            case 'MQTT':
                return [
                    { key: 'mqttTransportProtocol', label: 'Transport Protocol', type: 'select', options: [{ label: 'TCP', value: 'TCP' }, { label: 'TLS', value: 'TLS' }], required: true, defaultValue: 'TCP', infoText: 'Select the protocol used to establish the connection (TCP or TLS).' },
                    { key: 'mqttHost', label: 'Host', type: 'text', required: true, infoText: 'Server hostname or IP.' },
                    { key: 'mqttPort', label: 'Port', type: 'number', required: true, defaultValue: '', infoText: 'Port number of the server.' },
                    // { key: 'mqttBrokerUri', label: 'Broker URI', type: 'text', required: true, readOnly: true, infoText: 'Ensure the broker is reachable from this environment.' },
                    { key: 'mqttUsername', label: 'Username', type: 'text', required: true, infoText: 'Username for edge access.' },
                    { key: 'mqttPassword', label: 'Password', type: 'password', required: true, infoText: 'Password for edge access.' },
                    // { key: 'mqttTlsFilePath', label: 'Certificate File Path', type: 'text', required: false, infoText: 'Path to the Certificate Authority (CA) certificate used to verify the server’s identity.' },
                    ...commonFields,
                ];
            case 'OPCUA':
                return [
                    { key: 'opcuaTransportProtocol', label: 'Transport Protocol', type: 'select', options: [{ label: 'OPC.TCP', value: 'OPC.TCP' }, { label: 'HTTPS', value: 'opc.https' }], required: true, defaultValue: 'OPC.TCP', infoText: "Select the protocol used to establish the connection."},
                    { key: 'opcuaHost', label: 'Host', type: 'text', required: true, infoText: 'Server hostname or IP.' },
                    { key: 'opcuaPort', label: 'Port', type: 'number', required: true, infoText: 'Port number of the server.' },
                    // { key: 'opcuaPath', label: 'Path', type: 'text', required: false, infoText: 'Ensure the path exists and you have the required access permissions.' },
                    { key: 'opcuaServerUrl', label: 'Server URL', type: 'text', required: true, defaultValue: 'opc.tcp://', infoText: 'OPC UA endpoint address of the target server.' },
                    { key: 'opcuaMessageSecurityMode', label: 'Message Security Mode', type: 'select', options: [{ label: 'Sign and Encrypt', value: 'SignAndEncrypt' }, { label: 'Sign', value: 'Sign' }, { label: 'None', value: 'None' }], required: true, defaultValue: 'None', infoText: 'Defines how messages are secured' },
                    { key: 'opcuaSecurityPolicy', label: 'Security Policy', type: 'select', options: [{ label: 'Aes128_Sha256_RsaOaep', value: 'Aes128_Sha256_RsaOaep' }, { label: 'Aes256_Sha256_RsaPss', value: 'Aes256_Sha256_RsaPss' }, { label: 'Basic256Rsa256', value: 'Basic256Rsa256' }, { label: 'Basic128Rsa15', value: 'Basic128Rsa15' }, { label: 'Basic256', value: 'Basic256' }, { label: 'None', value: 'None' }], required: true, defaultValue: 'None', infoText: 'Specifies the encryption and signing algorithm to use.' },
                    { key: 'opcuaAuthenticationMode', label: 'User Authentication Mode', type: 'select', options: [{ label: 'Anonymous', value: 'anonymous' }, { label: 'Username & Password', value: 'username' }, { label: 'Certificate', value: 'certificate' }], required: true, defaultValue: 'Anonymous', infoText: 'Select the method used to authenticate this connection.' },
                    { key: 'opcuaUsername', label: 'Username', type: 'text', required: false },
                    { key: 'opcuaPassword', label: 'Password', type: 'password', required: false },
                    { key: 'opcuaCertificate', label: 'Certificate', type: 'file', required: false, accept: '.pem,.crt,.cer,.p12,.pfx' },
                    ...commonFields,
                ];
            case 'Modbus':
                return [
                    { key: 'ip21Host', label: 'Host', type: 'text', required: true },
                    { key: 'ip21Port', label: 'Port', type: 'text', required: true },
                    { key: 'ip21User', label: 'User', type: 'text', required: true },
                    { key: 'ip21Password', label: 'Password', type: 'password', required: true },
                    ...commonFields,
                ];
            case 'AVEVA PI':
                return commonFields;
            default:
                return commonFields;
        }
    };

    const initialValuesForForm = useMemo(() => {
        const resolvedNodeKind = type === 'UPDATE' ? initialValues?.nodeKind : 'OSIPI';
        return {
            edgeName: initialValues?.nodeName || '',
            nodeKind: resolvedNodeKind,
            storeForwardToggle: initialValues?.storeForwardToggle !== undefined ? initialValues.storeForwardToggle : true,
            enableScaling: getSecretFieldData(initialValues?.enableScaling) || false,
            scaleNodes: getSecretFieldData(initialValues?.scaleNodes) || 2,
            notificationEnabled: initialValues?.notificationEnabled || false,
            notificationEmail: Array.isArray(initialValues?.notificationEmail)
                ? initialValues.notificationEmail.join(',')
                : (initialValues?.notificationEmail || ''),
            autoResumeRequests: resolveAutoResumeRequests(initialValues?.autoResumeRequests, resolvedNodeKind),
        };
    }, [initialValues, type]);

    useEffect(() => {
        if (initialValuesForForm.storeForwardToggle !== undefined) {
            setStoreForwardToggle(initialValuesForForm.storeForwardToggle);
        }
        if (initialValuesForForm.enableScaling !== undefined) {
            setEnableScaling(initialValuesForForm.enableScaling);
        }
        if (initialValuesForForm.scaleNodes !== undefined) {
            setScaledNodesCount(initialValuesForForm.scaleNodes);
        }
        if (initialValuesForForm.notificationEnabled !== undefined) {
            setNotificationEnabled(initialValuesForForm.notificationEnabled);
        }
        setAutoResumeRequests(initialValuesForForm.autoResumeRequests);
    }, [initialValuesForForm.storeForwardToggle, initialValuesForForm.enableScaling, initialValuesForForm.scaleNodes, initialValuesForForm.notificationEnabled, initialValuesForForm.autoResumeRequests]);

    useEffect(() => {
        DataSourceHandler.GetNodeKind(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, (res) => {
            setNodeKindOptions(res.map((_res: any) => ({
                label: _res.key,
                value: _res.value
            })));
        });
    }, [Env]);

    useEffect(() => {
        if (initialValuesForForm.nodeKind) {
            setSelectedNodeKind(initialValuesForForm.nodeKind);
        }
    }, [initialValuesForForm.nodeKind]);

    // Helper to get field value from connectionDetails or top level (for backward compatibility)
    const getInitialFieldValue = (fieldKey: string) => {
        // First check connectionDetails
        if (initialValues?.connectionDetails?.[fieldKey] !== undefined) {
            return initialValues.connectionDetails[fieldKey];
        }
        // Fall back to top level for backward compatibility
        return initialValues?.[fieldKey];
    };

    useEffect(() => {
        if (selectedNodeKind) {
            const fields = getConfigurableFields(selectedNodeKind);
            const newTableFields: TableFieldItem[] = fields.map(field => {
                const initialFieldValue = getInitialFieldValue(field.key);
                const isSecret = isSecretField(initialFieldValue);
                const shouldClearValue = type === 'UPDATE' && (field.key === 'accessKey' || field.key === 'accessKeyConfirm');

                // For common fields (accessKey, accessKeyConfirm), preserve existing values if they exist
                const existingField = tableFields.find(f => f.key === field.key);
                const isCommonField = field.key === 'accessKey' || field.key === 'accessKeyConfirm';
                
                let fieldValue: string;
                let fieldUseSecret: boolean;
                
                if (isCommonField && existingField && existingField.value) {
                    // Preserve existing value and useSecret state for common fields
                    fieldValue = existingField.value;
                    fieldUseSecret = existingField.useSecret;
                } else {
                    fieldValue = shouldClearValue ? '' : (getSecretFieldData(initialFieldValue) || field.defaultValue || '');
                    fieldUseSecret = isSecret;
                }

                // Set dynamic label for rdbmsConnectionUrl based on rdbmsType
                let label = field.label;
                if (field.key === 'rdbmsConnectionUrl') {
                    const rdbmsType = getSecretFieldData(getInitialFieldValue('rdbmsType')) || 'SQL Server';
                    label = rdbmsType === 'MySQL' ? 'MySQL Url' : 'SQL Url';
                }

                return {
                    key: field.key,
                    label,
                    infoText: field.infoText,
                    useSecret: fieldUseSecret,
                    value: fieldValue,
                    required: field.required || false,
                    type: field.type,
                    options: field.options,
                    accept: field.accept,
                    file: null,
                    readOnly: field.readOnly || false,
                };
            });

            // Compute initial rdbmsConnectionUrl value
            if (selectedNodeKind === 'Relational') {
                const rdbmsType = getSecretFieldData(getInitialFieldValue('rdbmsType')) || 'SQL Server';
                const host = getSecretFieldData(getInitialFieldValue('rdbmsHost')) || '<hostname>';
                const port = getSecretFieldData(getInitialFieldValue('rdbmsPort')) || '<port>';
                const database = getSecretFieldData(getInitialFieldValue('rdbmsDatabase')) || '<database_name>';

                let connectionUrl = '';
                if (rdbmsType === 'SQL Server') {
                    connectionUrl = `jdbc:sqlserver://${host}:${port};databaseName=${database}`;
                } else if (rdbmsType === 'MySQL') {
                    connectionUrl = `jdbc:mysql://${host}:${port}/${database}`;
                }

                const urlFieldIndex = newTableFields.findIndex(f => f.key === 'rdbmsConnectionUrl');
                if (urlFieldIndex !== -1) {
                    newTableFields[urlFieldIndex].value = connectionUrl;
                }
            }

            // Compute initial mqttBrokerUri value
            if (selectedNodeKind === 'MQTT') {
                const protocol = getSecretFieldData(getInitialFieldValue('mqttTransportProtocol')) || 'TCP';
                const host = getSecretFieldData(getInitialFieldValue('mqttHost')) || '';
                const port = getSecretFieldData(getInitialFieldValue('mqttPort')) || '';

                const prefix = protocol === 'TLS' ? 'ssl' : 'tcp';
                const brokerUri = host ? `${prefix}://${host}${port ? `:${port}` : ''}` : `${prefix}://`;

                const uriFieldIndex = newTableFields.findIndex(f => f.key === 'mqttBrokerUri');
                if (uriFieldIndex !== -1) {
                    newTableFields[uriFieldIndex].value = brokerUri;
                }
            }

            setTableFields(newTableFields);
        } else {
            setTableFields([]);
        }
    }, [selectedNodeKind, initialValues, type]);

    const validateTableFields = (): { isValid: boolean; errors: string[] } => {
        const errors: string[] = [];
        const userAuthMode = tableFields.find(f => f.key === 'opcuaAuthenticationMode')?.value;

        tableFields.forEach(field => {
            // Skip validation for hidden fields
            if (!isFieldVisible(field.key)) return;

            // Check conditional required fields
            const isConditionallyRequired =
                ((field.key === 'opcuaUsername' || field.key === 'opcuaPassword') && userAuthMode === 'username') ||
                (field.key === 'opcuaCertificate' && userAuthMode === 'certificate');

            const isRequired = field.required || isConditionallyRequired;

            if (isRequired) {
                const hasValue = field.value && field.value.trim() !== '';
                if (!hasValue) {
                    errors.push(`${field.label} is required`);
                }
            }
        });

        const accessKeyField = tableFields.find(f => f.key === 'accessKey');
        const accessKeyConfirmField = tableFields.find(f => f.key === 'accessKeyConfirm');
        if (accessKeyField && accessKeyConfirmField) {
            if (accessKeyField.value && accessKeyConfirmField.value && accessKeyField.value !== accessKeyConfirmField.value) {
                errors.push('Access keys must match');
            }
        }

        return { isValid: errors.length === 0, errors };
    };

    const handleFormSubmit = (values: NifiFormValues) => {
        setSubmitAttempted(true);
        const { isValid, errors } = validateTableFields();

        if (!isValid) {
            // errorAlert(errors.join(', '));
            return;
        }

        const secretFields: Record<string, SecretFieldConfig> = {};
        tableFields.forEach(field => {
            secretFields[field.key] = {
                useSecret: field.useSecret,
                data: field.value || '',
                secretManagerId: field.useSecret && secretManagerId ? secretManagerId : null
            };
        });

        const notificationEmail = notificationEnabled && values.notificationEmail
            ? values.notificationEmail.split(',').map((email) => email.trim()).filter(Boolean)
            : [];

        const submitValues = {
            edgeName: values.edgeName,
            nodeKind: values.nodeKind,
            storeForwardToggle: storeForwardToggle,
            enableScaling: selectedNodeKind === 'Modbus' ? false : enableScaling,
            scaleNodes: selectedNodeKind === 'Modbus' ? null : scaledNodesCount,
            notificationEnabled,
            notificationEmail,
            autoResumeRequests,
            secretFields
        };

        onSubmit && onSubmit(submitValues as any);
    };

    const updateFieldFile = (key: string, file: File | null) => {
        if (!file) {
            setTableFields(prev => prev.map(field =>
                field.key === key ? { ...field, file: null, fileName: '', value: '' } : field
            ));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const base64Data = result.includes(',') ? result.split(',')[1] : result;
            setTableFields(prev => prev.map(field =>
                field.key === key ? { ...field, file, fileName: file.name, value: base64Data } : field
            ));
        };
        reader.onerror = () => {
            setTableFields(prev => prev.map(field =>
                field.key === key ? { ...field, file: null, fileName: '', value: '' } : field
            ));
        };
        reader.readAsDataURL(file);
    };

    const updateFieldValue = (key: string, value: string) => {
        setTableFields(prev => {
            let updatedFields = prev.map(field =>
                field.key === key ? { ...field, value } : field
            );

            // Handle dependency: opcuaMessageSecurityMode -> opcuaSecurityPolicy
            if (key === 'opcuaMessageSecurityMode') {
                updatedFields = updatedFields.map(field => {
                    if (field.key === 'opcuaSecurityPolicy') {
                        // If None is selected for security mode, set security policy to None
                        if (value === 'None') {
                            return { ...field, value: 'None' };
                        } else if (field.value === 'None') {
                            // If switching from None to Sign/SignAndEncrypt, reset to first non-None option
                            return { ...field, value: 'Aes128_Sha256_RsaOaep' };
                        }
                    }
                    return field;
                });
            }

            // Handle dependency: Compute rdbmsConnectionUrl based on rdbmsType, host, port, database
            if (['rdbmsType', 'rdbmsHost', 'rdbmsPort', 'rdbmsDatabase'].includes(key)) {
                const rdbmsType = key === 'rdbmsType' ? value : updatedFields.find(f => f.key === 'rdbmsType')?.value || 'SQL Server';
                const host = key === 'rdbmsHost' ? value : updatedFields.find(f => f.key === 'rdbmsHost')?.value || '<hostname>';
                const port = key === 'rdbmsPort' ? value : updatedFields.find(f => f.key === 'rdbmsPort')?.value || '<port>';
                const database = key === 'rdbmsDatabase' ? value : updatedFields.find(f => f.key === 'rdbmsDatabase')?.value || '<database_name>';

                let connectionUrl = '';
                let urlLabel = 'Connection URL';
                if (rdbmsType === 'SQL Server') {
                    connectionUrl = `jdbc:sqlserver://${host || '<hostname>'}:${port || '<port>'};databaseName=${database || '<database_name>'}`;
                    urlLabel = 'SQL Url';
                } else if (rdbmsType === 'MySQL') {
                    connectionUrl = `jdbc:mysql://${host || '<hostname>'}:${port || '<port>'}/${database || '<database_name>'}`;
                    urlLabel = 'MySQL Url';
                }

                updatedFields = updatedFields.map(field =>
                    field.key === 'rdbmsConnectionUrl' ? { ...field, value: connectionUrl, label: urlLabel } : field
                );
            }

            // Handle dependency: Compute mqttBrokerUri based on mqttTransportProtocol, host, port
            if (['mqttTransportProtocol', 'mqttHost', 'mqttPort'].includes(key)) {
                const protocol = key === 'mqttTransportProtocol' ? value : updatedFields.find(f => f.key === 'mqttTransportProtocol')?.value || 'TCP';
                const host = key === 'mqttHost' ? value : updatedFields.find(f => f.key === 'mqttHost')?.value || '';
                const port = key === 'mqttPort' ? value : updatedFields.find(f => f.key === 'mqttPort')?.value || '';

                const prefix = protocol === 'TLS' ? 'ssl' : 'tcp';
                const brokerUri = host ? `${prefix}://${host}${port ? `:${port}` : ''}` : `${prefix}://`;

                updatedFields = updatedFields.map(field =>
                    field.key === 'mqttBrokerUri' ? { ...field, value: brokerUri } : field
                );
            }

            return updatedFields;
        });
    };

    const getSecurityPolicyOptions = (messageSecurityMode: string, originalOptions: { label: string; value: string }[]) => {
        if (messageSecurityMode === 'None') {
            // Only None is enabled when message security mode is None
            return originalOptions.map(opt => ({
                ...opt,
                disabled: opt.value !== 'None'
            }));
        } else {
            // All options except None are enabled for Sign/SignAndEncrypt
            return originalOptions.map(opt => ({
                ...opt,
                disabled: opt.value === 'None'
            }));
        }
    };

    // Check if a field should be visible based on dependencies
    const isFieldVisible = (fieldKey: string): boolean => {
        const userAuthMode = tableFields.find(f => f.key === 'opcuaAuthenticationMode')?.value;
        const mqttTransportProtocol = tableFields.find(f => f.key === 'mqttTransportProtocol')?.value;

        switch (fieldKey) {
            case 'opcuaUsername':
            case 'opcuaPassword':
                // Show only when userAuthenticationMode is 'username'
                return userAuthMode === 'username';
            case 'opcuaCertificate':
                // Show only when userAuthenticationMode is 'certificate'
                return userAuthMode === 'certificate';
            case 'mqttTlsFilePath':
                // Show only when mqttTransportProtocol is 'TLS'
                return mqttTransportProtocol === 'TLS';
            default:
                return true;
        }
    };

    const toggleUseSecret = (key: string) => {
        if (secretsList.length === 0 || secretsLoading) return;

        const accessKeyPair = ['accessKey', 'accessKeyConfirm'];
        const shouldSyncToggle = accessKeyPair.includes(key);

        setTableFields(prev => {
            const currentField = prev.find(f => f.key === key);
            const newUseSecretValue = !currentField?.useSecret;

            return prev.map(field => {
                if (shouldSyncToggle && accessKeyPair.includes(field.key)) {
                    return { ...field, useSecret: newUseSecretValue };
                }
                if (field.key === key) {
                    return { ...field, useSecret: newUseSecretValue };
                }
                return field;
            });
        });
    };

    const getFieldError = (field: TableFieldItem): string => {
        if (!submitAttempted) return '';

        // Check if field is visible - if not visible, no error
        if (!isFieldVisible(field.key)) return '';

        // Check conditional required fields
        const userAuthMode = tableFields.find(f => f.key === 'opcuaAuthenticationMode')?.value;
        const isConditionallyRequired =
            ((field.key === 'opcuaUsername' || field.key === 'opcuaPassword') && userAuthMode === 'username') ||
            (field.key === 'opcuaCertificate' && userAuthMode === 'certificate');

        const isRequired = field.required || isConditionallyRequired;

        if (isRequired && (!field.value || field.value.trim() === '')) {
            return `${field.label} is required`;
        }

        if (field.key === 'accessKeyConfirm' && field.value) {
            const accessKeyField = tableFields.find(f => f.key === 'accessKey');
            if (accessKeyField && accessKeyField.value && accessKeyField.value !== field.value) {
                return 'Access keys must match';
            }
        }

        return '';
    };

    return (
        <Form
            initialValues={initialValuesForForm}
            validationSchema={nifiFormSchema}
            onSubmit={handleFormSubmit}
            enableReinitialize
        >
            {({ _formikprops: { values, setFieldValue } }) => (
                <>
                    <div className={styles['userInfoForm']}>
                        <InputField
                            name="edgeName"
                            label="Node Name"
                            required={true}
                            autoComplete="off"
                            className="mb-0"
                            disabled={type === 'UPDATE'}
                            infoText='Enter a unique name for this node.'
                        />
                        <SelectField
                            name="nodeKind"
                            label="Node Kind"
                            options={nodeKindOptions}
                            initial_value={values.nodeKind}
                            required={true}
                            className="mb-0"
                            onOptionClick={(opt) => {
                                setSelectedNodeKind(opt.value);
                                setFieldValue('nodeKind', opt.value);
                                setAutoResumeRequests(getAutoResumeDefault(opt.value));
                            }}
                            infoText='Select the type of node from the list.'
                        />

                        <ShowWhenTrue show={!isEmpty(values.nodeKind) && tableFields.length > 0}>
                            <div>
                                <div className={styles['edge-table__header']}>
                                    <Text className={`${styles['table-secret-label']} ${styles['table-secret-label--field']}`}>
                                        Field
                                    </Text>
                                    <Text className={`${styles['table-secret-label']} ${styles['table-secret-label--secret']}`}>
                                        Use Secret
                                    </Text>
                                    <Text className={`${styles['table-secret-label']} ${styles['table-secret-label--value']}`}>
                                        Value
                                    </Text>
                                </div>

                                <div className={styles['edge-table__body']}>
                                    {tableFields.filter(field => isFieldVisible(field.key)).map((field) => {
                                        const errorMessage = getFieldError(field);
                                        const showError = !!errorMessage;
                                        const secretSelectOptions: _selectoptionType[] = secretsList.map(s => ({
                                            label: s,
                                            value: s
                                        }));

                                        return (
                                            <div
                                                key={field.key}
                                                className={`${styles['edge-table__row']} ${showError ? styles['edge-table__row--error'] : ''}`}
                                            >
                                                <div className={styles['edge-table__col-field']}>
                                                    <span className={`${styles['edge-table__field-label']} ${field.required ? 'red-star' : ''}`}>
                                                        {field.label}
                                                        <ShowWhenTrue show={!!field.infoText}>
                                                            <TooltipTop placement="topRight" overlay={field.infoText}>
                                                                <img src="/icons/info-fields.png" width="16" height="16" className="info__icon" alt="information-icon" />
                                                            </TooltipTop>
                                                        </ShowWhenTrue>
                                                    </span>
                                                </div>

                                                <div className={styles['edge-table__col-secret']}>
                                                    <ToggleField
                                                        active={field.useSecret}
                                                        onClick={() => toggleUseSecret(field.key)}
                                                        disabled={secretsList.length === 0 || secretsLoading}
                                                    />
                                                </div>

                                                <div className={styles['edge-table__col-value']}>
                                                    {field.useSecret ? (
                                                        <SelectField
                                                            name={`secret_${field.key}`}
                                                            options={secretSelectOptions}
                                                            initial_value={field.value}
                                                            placeholder="Select secret"
                                                            onOptionClick={(opt) => updateFieldValue(field.key, opt.value)}
                                                            disabled = {type ==="UPDATE" && field.key === "mqttTlsFilePath"}
                                                            className="mb-0"
                                                        />
                                                    ) : (
                                                        field.type === 'select' && field.options ? (
                                                            <SelectField
                                                                name={`value_${field.key}`}
                                                                options={
                                                                    field.key === 'opcuaSecurityPolicy'
                                                                        ? getSecurityPolicyOptions(
                                                                            tableFields.find(f => f.key === 'opcuaMessageSecurityMode')?.value || 'None',
                                                                            field.options
                                                                        )
                                                                        : field.options.map(opt => ({ label: opt.label, value: opt.value }))
                                                                }
                                                                initial_value={field.value}
                                                                placeholder={`Select ${field.label.toLowerCase()}`}
                                                                onOptionClick={(opt) => updateFieldValue(field.key, opt.value)}
                                                                className="mb-0"
                                                            />
                                                        ) : field.type === 'file' ? (
                                                            <div className={styles['edge-table__file-input']}>
                                                                <input
                                                                    type="file"
                                                                    id={`file_${field.key}`}
                                                                    accept={field.accept}
                                                                    onChange={(e) => {
                                                                        const file = e.target.files?.[0] || null;
                                                                        updateFieldFile(field.key, file);
                                                                    }}
                                                                    className={styles['edge-table__file']}
                                                                />
                                                                <label htmlFor={`file_${field.key}`} className={`inputfield ${styles['edge-table__input']} ${styles['edge-table__file-label']}`}>
                                                                    {field.fileName || 'Choose file...'}
                                                                </label>
                                                            </div>
                                                        ) : (
                                                            <input
                                                                type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                                                                className={`inputfield ${styles['edge-table__input']} ${showError ? 'inputfield--error' : ''} ${field.readOnly ? styles['edge-table__input--readonly'] : ''}`}
                                                                value={field.value}
                                                                onChange={(e) => !field.readOnly && updateFieldValue(field.key, e.target.value)}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                autoComplete="off"
                                                                min={field.type === 'number' ? 0 : undefined}
                                                                readOnly={field.readOnly}
                                                                disabled={type === "UPDATE" && field.key === "mqttTlsFilePath"}
                                                            />
                                                        )
                                                    )}
                                                    {showError && (
                                                        <span className={styles['edge-table__error']}>
                                                            {errorMessage}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </ShowWhenTrue>

                        <ShowWhenTrue show={(selectedNodeKind === 'OPCUA' && type === 'ADD') || (type === 'UPDATE' && initialValues?.nodeKind === 'OPCUA') || (type === 'UPDATE' && selectedNodeKind === 'OPCUA')}>
                            <div className={`${styles['form-item']} ${styles['toggle-field-row']}`}>
                                <Flex alignItems="center" gap="10px">
                                    <label className={styles['toggle-field-row__label']}>Store & Forward</label>
                                    <ToggleField
                                        active={storeForwardToggle}
                                        onClick={() => setStoreForwardToggle(!storeForwardToggle)}
                                        disabled={type === 'UPDATE'}
                                        label={storeForwardToggle ? 'Enabled' : 'Disabled'}
                                    />
                                </Flex>
                            </div>
                        </ShowWhenTrue>

                        <ShowWhenTrue show={selectedNodeKind !== 'Modbus' && !isEmpty(values.nodeKind)}>
                            <div className={`${styles['form-item']} ${styles['toggle-field-row']}`}>
                                <Flex alignItems="center" gap="10px">
                                    <label className={styles['toggle-field-row__label']}>Enable Scaling</label>
                                    <ToggleField
                                        active={enableScaling}
                                        onClick={() => setEnableScaling(!enableScaling)}
                                        disabled={type === 'UPDATE'}
                                        label={enableScaling ? 'Enabled' : 'Disabled'}
                                    />
                                </Flex>
                            </div>
                        </ShowWhenTrue>

                        {(enableScaling || initialValuesForForm.enableScaling) && selectedNodeKind !== 'Modbus' && (
                            <SelectField
                                name="scaleNodes"
                                label="No. of Scaled Nodes"
                                options={Array(9).fill(0).map((_, index) => {
                                    const optionValue = index + 2;
                                    const currentValue = scaledNodesCount ?? initialValuesForForm.scaleNodes ?? 2;
                                    return {
                                        label: String(optionValue),
                                        value: optionValue,
                                        disabled: optionValue < currentValue
                                    };
                                })}
                                initial_value={scaledNodesCount !== undefined && scaledNodesCount !== null ? scaledNodesCount : initialValuesForForm.scaleNodes ?? 2}
                                onOptionClick={(opt) => {
                                    const currentValue = scaledNodesCount ?? initialValuesForForm.scaleNodes ?? 2;
                                    if (opt.value < currentValue) {
                                        infoAlert("Please delete the nodes instead of selecting shorter number.");
                                    } else {
                                        setScaledNodesCount(opt.value);
                                    }
                                }}
                                className="mb-0"
                            />
                        )}

                        <ShowWhenTrue show={!isEmpty(values.nodeKind)}>
                            <div className={`${styles['form-item']} ${styles['toggle-field-row']}`}>
                                <Flex alignItems="center" gap="10px">
                                    <label className={styles['toggle-field-row__label']}>Auto-Resume Requests</label>
                                    <ToggleField
                                        active={autoResumeRequests}
                                        onClick={() => setAutoResumeRequests(!autoResumeRequests)}
                                        label={autoResumeRequests ? 'Enabled' : 'Disabled'}
                                    />
                                    <TooltipTop placement="topRight" overlay="Automatically resume requests when edge reconnects">
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 12,
                                                height: 12,
                                                minWidth: 12,
                                                flexShrink: 0,
                                                borderRadius: '50%',
                                                backgroundColor: 'white',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <img src="/icons/info-fields.png" width={12} height={12} alt="information-icon" />
                                        </span>
                                    </TooltipTop>
                                </Flex>
                            </div>
                        </ShowWhenTrue>

                        <div className={`${styles['form-item']} ${styles['toggle-field-row']}`}>
                            <Flex alignItems="center" gap="10px">
                                <label className={styles['toggle-field-row__label']}>Email Alert</label>
                                <ToggleField
                                    active={notificationEnabled}
                                    onClick={() => setNotificationEnabled(!notificationEnabled)}
                                    label={notificationEnabled ? 'Enabled' : 'Disabled'}
                                />
                            </Flex>
                        </div>

                        {(notificationEnabled || initialValuesForForm.notificationEnabled) && (
                            <>
                                <SelectField
                                    name="notificationEmail"
                                    label="Select Users"
                                    placeholder="Select users"
                                    options={notificationUserOptions}
                                    initial_value={initialValuesForForm.notificationEmail}
                                    multiple_select
                                    className="mb-0"
                                />
                                <Flex alignItems="center" gap="8px" className={styles['notificationInfoBanner']}>
                                    <InformativeIcon />
                                    <Text fontSize={12} fontWeight={500}>
                                        If no users are selected, notifications will be sent to your email ({activeUserInfo?.email}) by default. This notification is only on failure.
                                    </Text>
                                </Flex>
                            </>
                        )}
                    </div>
                    {!!submitButtons && submitButtons}
                </>
            )}
        </Form>
    );
};
